/** @jsxImportSource @emotion/react */
import type { FunctionInterpolation, Interpolation } from "@emotion/react";
import { I18nMessage, useFormatter, useTranslations } from "@i18n/react";
import type { TheWordGame } from "@packages/sdk";

import {
  constantSemanticColors,
  spacing,
  type ThemeType,
} from "../../../../../global/emotion";
import { cssMediaQueryMinWidthTablet } from "../../../../lib/constants/emotionMediaQueries.constants";
import type { CardProps } from "../../../refactor/Card/Card.emotion";
import { Card } from "../../../refactor/Card/Card.emotion";
import { Text } from "../../_core/Text/Text.emotion";
import { TheWordAnswerScore } from "./TheWordScore.emotion";
import { TheWordShareIconButton } from "./TheWordShareIconButton";

const cardStyle: Interpolation = {
  paddingBottom: "96px",
  paddingTop: `calc(${spacing.full} - ${spacing.xs})`,
};

const imgContainerStyle: FunctionInterpolation<{
  color: TheWordMediumProps["color"];
}> = (props) => ({
  alignItems: "center",
  backgroundColor: props.color,
  display: "flex",
  justifyContent: "center",
});
const infoContainerStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const maskStyle: Interpolation = {
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
};
const cssDivIsFinishedContentContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  color: theme.colors.white,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.l,
  height: "100%",
  justifyContent: "space-between",
  padding: theme.spacing.l,
});
const cssDivContentContainerGroupContainer: Interpolation<ThemeType> = (
  theme,
) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing.s,
  justifyContent: "space-between",
  width: "100%",
});
const cssDivMatrixContainer: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing.m,
});
const cssTextBody: Interpolation<ThemeType> = (theme) => ({
  color: theme.tints.tint80,
  textAlign: "center",
});
const guessesContainerStyle: Interpolation = {
  alignItems: "center",
  alignSelf: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
  justifyContent: "center",
};
const summaryContainerStyle: Interpolation = {
  display: "flex",
  gap: spacing.s,
  justifyContent: "space-around",
  paddingInline: "15px",
  width: spacing.full,
};
const summaryStatisticContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  textAlign: "center",
};
const summaryStatisticLabelStyle: Interpolation = {
  color: constantSemanticColors.onColorVeryHigh,
  whiteSpace: "normal",
  [cssMediaQueryMinWidthTablet]: {
    whiteSpace: "nowrap",
  },
};

export type TheWordMediumProps = CardProps & {
  game: TheWordGame;
  won?: boolean;
};

export const TheWordMedium = ({
  centerActions,
  color,
  description,
  imgContainerCSS,
  imgSrc,
  infoContainerCSS,
  maskProps,
  game,
  title,
  won,
  ...props
}: TheWordMediumProps) => {
  const summary = game?.summary ?? null;
  const t = useTranslations();
  const format = useFormatter();

  return (
    <Card
      centerActions={summary ? null : centerActions}
      color={color}
      description={summary ? null : description}
      imgContainerCSS={[imgContainerStyle({ color }), imgContainerCSS]}
      imgSrc={summary ? null : imgSrc}
      infoContainerCSS={[infoContainerStyle, infoContainerCSS]}
      maskContainerCSS={maskStyle}
      maskCSS={summary ? { display: "none" } : null}
      maskProps={maskProps}
      overlay={
        summary ? (
          <div css={cssDivIsFinishedContentContainer}>
            <div css={cssDivContentContainerGroupContainer}>
              <Text size="l" type="title">
                <I18nMessage message="the_word_finished_title" />
              </Text>
              <TheWordShareIconButton game={game} />
            </div>
            <div css={cssDivMatrixContainer}>
              <div key="scoresContainer" css={guessesContainerStyle}>
                {summary.matrix.map((guess, index) => (
                  <TheWordAnswerScore
                    key={index}
                    guess={guess}
                    index={index}
                    size="m"
                  />
                ))}
              </div>
              <Text css={cssTextBody} size="m" type="body">
                {description}
              </Text>
            </div>
            <div key="summaryContainer" css={summaryContainerStyle}>
              {[
                {
                  label: t("web_the_word_summary_win_percentage"),
                  value: format.number(summary.win_percentage / 100, {
                    style: "percent",
                  }),
                },
                {
                  label: t("trivia_current_streak"),
                  value: format.number(summary.streak),
                },
                {
                  label: t("trivia_max_streak"),
                  value: format.number(summary.longest_streak),
                },
              ].map((statistic) => (
                <div key={statistic.label} css={summaryStatisticContainerStyle}>
                  <Text type="title" size="l">
                    {statistic.value}
                  </Text>
                  <Text
                    type="detail"
                    size="xl"
                    css={summaryStatisticLabelStyle}
                  >
                    {statistic.label}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        ) : null
      }
      css={cardStyle}
      title={summary ? null : title}
      {...props}
    />
  );
};
